import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { Button } from '../atoms/button/button';
import { InfoBox } from 'tradera/components/info-box/info-box';
import ArrowRightIcon from 'tradera/blueprint/theme/icons2/arrow-full-right.svg?jsx';

const trackPaymentOnboarding = (action: string, label: string) => {
    GtmService.trackAction('Tradera Payment Onboarding', action, label);
};

const className = 'd-flex mx-2 my-1 w-100';

const InvoiceUnpaidAlert = () => {
    const { t } = useTranslator();
    return (
        <InfoBox className={className} alert>
            {t('siteheader_invoiceUnpaidDescription')}
        </InfoBox>
    );
};

const InvoiceOverdueAlert = () => {
    const { t } = useTranslator();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_invoiceOverdueTitle')}>
            {t('siteheader_invoiceOverdueDescription')}
        </InfoBox>
    );
};

const PayoutFailedAlert = () => {
    const { t } = useTranslator();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_payoutFailedTitle')}>
            {t('siteheader_payoutFailedDescription')}
        </InfoBox>
    );
};

const PayoutReadyBankAccountMissingAlert = () => {
    const { t } = useTranslator();
    trackPaymentOnboarding(
        'Bank account Onboarding Alert',
        'Will add from - Account menu alert - Failed payment'
    );
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_paymentReadBankAccountMissingTitle')}>
            {t('siteheader_paymentReadBankAccountMissingDescription')}
        </InfoBox>
    );
};

const HyperwalletPayeeVerificationAlert = () => {
    const { t } = useTranslator();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_hyperwalletPayeeVerificationTitle')}
            actions={
                <Button
                    theme="action"
                    variant="outline"
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/my/PayeeVerification';
                    }}>
                    {t('siteheader_hyperwalletPayeeVerificationMoreInfo')}
                </Button>
            }>
            {t('siteheader_hyperwalletPayeeVerificationDescription')}
        </InfoBox>
    );
};

const HyperwalletPayeeVerificationReviewAlert = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            info
            alert
            header={t('siteheader_hyperwalletPayeeVerificationReviewTitle')}
            actions={
                <Button
                    theme="action"
                    variant="outline"
                    icon={<ArrowRightIcon />}
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = toLocalizedUrl(
                            '/my/PayeeVerificationCompleted'
                        );
                    }}
                />
            }>
            {t('siteheader_hyperwalletPayeeVerificationReviewDescription')}
        </InfoBox>
    );
};

const KycQuestionnaireAlert = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            info
            alert
            header={t('siteheader_kycQuestionnaireTitle')}
            actions={
                <Button
                    theme="action"
                    variant="outline"
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = toLocalizedUrl(
                            '/verify/kyc-questionnaire'
                        );
                    }}>
                    {t('siteheader_kycQuestionnaireMoreInfo')}
                </Button>
            }>
            {t('siteheader_kycQuestionnaireDescription')}
        </InfoBox>
    );
};

const PayoutReadyVerificationRequiredAlert = () => {
    const { t } = useTranslator();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_payoutReadyVerificationRequiredTitle')}>
            {t('siteheader_payoutReadyVerificationRequiredDescription')}
        </InfoBox>
    );
};

const OutstandingTraderaPayLicenseAlert = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            info
            alert
            header={t('siteheader_outstandingTraderaPayLicenseTitle')}
            actions={
                <Button
                    theme="action"
                    variant="outline"
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = toLocalizedUrl(
                            '/my/settings/payouts'
                        );
                    }}>
                    {t('siteheader_outstandingTraderaPayLicenseContinue')}
                </Button>
            }>
            {t('siteheader_outstandingTraderaPayLicenseDescription')}
        </InfoBox>
    );
};

const PayoutReadyOutstandingTraderaPayLicenseAlert = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t(
                'siteheader_payoutReadyOutstandingTraderaPayLicenseTitle'
            )}
            actions={
                <Button
                    theme="action"
                    variant="outline"
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = toLocalizedUrl(
                            '/my/settings/payouts'
                        );
                    }}>
                    {t(
                        'siteheader_payoutReadyOutstandingTraderaPayLicenseContinue'
                    )}
                </Button>
            }>
            {t('siteheader_payoutReadyOutstandingTraderaPayLicenseDescription')}
        </InfoBox>
    );
};

const CollectVatNumberAlert = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_collectVatNumberTitle')}
            actions={
                <Button
                    theme="action"
                    variant="outline"
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = toLocalizedUrl(
                            '/verify/collect-vat-number'
                        );
                    }}>
                    {t('siteheader_collectVatNumberContinue')}
                </Button>
            }>
            {t('siteheader_collectVatNumberDescription')}
        </InfoBox>
    );
};

const CollectTinAlert = () => {
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_collectTinTitle')}
            actions={
                <Button
                    theme="action"
                    variant="outline"
                    onClick={(e) => {
                        e.preventDefault();
                        window.location.href = toLocalizedUrl(
                            '/verify/collect-tin'
                        );
                    }}>
                    {t('siteheader_collectTinContinue')}
                </Button>
            }>
            {t('siteheader_collectTinDescription')}
        </InfoBox>
    );
};

const PayoutProviderOnboardingFailedAlert = () => {
    const { t } = useTranslator();
    return (
        <InfoBox
            className={className}
            headerClassName="size-paris"
            danger
            alert
            header={t('siteheader_payoutProviderOnboardingFailedTitle')}>
            {t('siteheader_payoutProviderOnboardingFailedDescription')}
        </InfoBox>
    );
};

export {
    InvoiceUnpaidAlert,
    InvoiceOverdueAlert,
    PayoutFailedAlert,
    PayoutReadyBankAccountMissingAlert,
    HyperwalletPayeeVerificationAlert,
    HyperwalletPayeeVerificationReviewAlert,
    KycQuestionnaireAlert,
    PayoutReadyVerificationRequiredAlert,
    OutstandingTraderaPayLicenseAlert,
    PayoutReadyOutstandingTraderaPayLicenseAlert,
    CollectVatNumberAlert,
    CollectTinAlert,
    PayoutProviderOnboardingFailedAlert
};
